import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaAws, FaCertificate, FaExternalLinkAlt, FaCalendarAlt, FaGoogle, FaJava, FaTasks } from 'react-icons/fa';
import { useLanguage } from '../utils/LanguageContext';
import './AllComponents.css';

const Certificates = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });

  const { t } = useLanguage();

  const certificates = [
    {
      name: t('certificates.certificateData.aws.name'),
      issuer: t('certificates.certificateData.aws.issuer'),
      date: "Jan 2025",
      credentialId: null,
      description: t('certificates.certificateData.aws.description'),
      icon: <FaAws />,
      verifyUrl: "https://aws.amazon.com/certification/verify",
      skills: ["AI/ML", "AWS AI Services", "Machine Learning", "Cloud AI"]
    },
    {
      name: t('certificates.certificateData.scrum.name'),
      issuer: t('certificates.certificateData.scrum.issuer'),
      date: "Dec 2023",
      credentialId: null,
      description: t('certificates.certificateData.scrum.description'),
      icon: <FaTasks />,
      verifyUrl: "https://www.scrum.org/certificates",
      skills: ["Agile Methods", "JIRA", "Scrum", "Program Management"]
    },
    {
      name: t('certificates.certificateData.apigee.name'),
      issuer: t('certificates.certificateData.apigee.issuer'),
      date: "Oct 2022",
      credentialId: null,
      description: t('certificates.certificateData.apigee.description'),
      icon: <FaGoogle />,
      verifyUrl: "https://cloud.google.com/certification",
      skills: ["API Development", "Apigee", "Google Cloud", "API Management"]
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="certificates" className="section certificates-section">
      <div className="container">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.div variants={itemVariants} className="section-header text-center">
            <h2 className="gradient-text">{t('certificates.certifications')}</h2>
            <p className="section-subtitle">
              {t('certificates.certificatesSubtitle')}
            </p>
          </motion.div>

          <div className="certificates-grid">
            {certificates.map((cert, index) => (
              <motion.div
                key={index}
                className="certificate-card card"
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
              >
                <div className="certificate-header">
                  <div className="cert-icon">
                    {cert.icon}
                  </div>
                  <div className="cert-badge">
                    <FaCertificate />
                  </div>
                </div>

                <div className="certificate-content">
                  <h3>{cert.name}</h3>
                  <h4>{cert.issuer}</h4>
                  <p className="cert-description">{cert.description}</p>

                  <div className="cert-meta">
                    <div className="meta-item">
                      <FaCalendarAlt />
                      <span>{t('certificates.issued')}: {cert.date}</span>
                    </div>
                    {cert.credentialId && (
                      <div className="meta-item">
                        <span>ID: {cert.credentialId}</span>
                      </div>
                    )}
                  </div>

                  <div className="cert-skills">
                    {cert.skills.map((skill, skillIndex) => (
                      <span key={skillIndex} className="skill-tag">
                        {skill}
                      </span>
                    ))}
                  </div>

                  <a 
                    href={cert.verifyUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="verify-btn btn btn-outline"
                  >
                    <FaExternalLinkAlt />
                    {t('certificates.verifyCertificate')}
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Certificates; 